'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const quickLinks = [
    { name: 'Home', href: '/homepage' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Services', href: '/services-overview' },
    { name: 'Contact', href: '/contact-and-support' }
  ];

  const services = [
    { name: 'Telecom Tower Installation', href: '/services-overview' },
    { name: 'Electrical Systems', href: '/services-overview' },
    { name: 'AMC & Maintenance', href: '/services-overview' },
    { name: 'Emergency Support', href: '/contact-and-support' }
  ];

  const contactInfo = [
    { icon: 'UserIcon', text: 'Mr. Vipin Gupta', href: '#' },
    { icon: 'PhoneIcon', text: '+91 80599 00182', href: 'tel:+918059900182' },
    { icon: 'EnvelopeIcon', text: 'info@ativanam.com', href: 'mailto:info@ativanam.com' },
    { icon: 'MapPinIcon', text: 'Sonepat, Haryana', href: '#' }
  ];

  if (!isHydrated) {
    return (
      <footer className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-sm">© 2026 Ativanam Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="40" height="40" rx="8" fill="white" />
                  <path
                    d="M12 28L20 12L28 28H24L20 20L16 28H12Z"
                    fill="var(--color-primary)"
                  />
                  <circle cx="20" cy="16" r="2" fill="var(--color-accent)" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-headline font-bold leading-tight">
                  Ativanam
                </span>
                <span className="text-xs font-body leading-tight opacity-80">
                  Solutions
                </span>
              </div>
            </div>
            <p className="text-sm opacity-80 font-body leading-relaxed mb-4">
              Your trusted partner for mission-critical infrastructure solutions. Delivering technical excellence with proven reliability since 2010.
            </p>
            <div className="flex items-center gap-2">
              <Icon name="CheckBadgeIcon" size={20} className="text-accent" />
              <span className="text-xs font-body opacity-80">ISO 9001:2015 Certified</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm font-body opacity-80 hover:opacity-100 hover:text-accent transition-all duration-300 inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="mr-1 group-hover:translate-x-1 transition-transform duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    href={service.href}
                    className="text-sm font-body opacity-80 hover:opacity-100 hover:text-accent transition-all duration-300 inline-flex items-center group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="mr-1 group-hover:translate-x-1 transition-transform duration-300" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              {contactInfo.map((info, index) => (
                <li key={index}>
                  <a
                    href={info.href}
                    className="text-sm font-body opacity-80 hover:opacity-100 hover:text-accent transition-all duration-300 flex items-start gap-2 group"
                  >
                    <Icon name={info.icon as any} size={18} className="flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300" />
                    <span>{info.text}</span>
                  </a>
                </li>
              ))}
            </ul>

            {/* Emergency Support Badge */}
            <div className="mt-6 p-4 bg-cta rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-white" />
                <span className="text-sm font-headline font-bold">24/7 Emergency</span>
              </div>
              <p className="text-xs opacity-90 font-body">
                Critical infrastructure support available round the clock
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm font-body opacity-80 text-center md:text-left">
              © {currentYear} Ativanam Solutions. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                href="/services-overview"
                className="text-sm font-body opacity-80 hover:opacity-100 hover:text-accent transition-all duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                href="/services-overview"
                className="text-sm font-body opacity-80 hover:opacity-100 hover:text-accent transition-all duration-300"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;